import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileQueue = ({ files, onRemoveFile, onClearAll, isProcessing, autoAnalyze = false }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return { name: 'FileText', color: 'var(--color-error)' };
      case 'doc': case'docx':
        return { name: 'FileText', color: 'var(--color-secondary)' };
      default:
        return { name: 'File', color: 'var(--color-muted-foreground)' };
    }
  };

  const getFileStatus = (file) => {
    if (file?.status === 'error') {
      return {
        icon: 'AlertCircle',
        color: 'var(--color-error)',
        text: file?.error || 'Upload failed',
        bgColor: 'bg-error/10',
        textColor: 'text-error'
      };
    }
    if (file?.status === 'processing') {
      return {
        icon: 'Loader2',
        color: 'var(--color-primary)',
        text: 'Analyzing...',
        bgColor: 'bg-primary/10',
        textColor: 'text-primary',
        animate: true
      };
    }
    if (file?.status === 'complete') {
      return {
        icon: 'CheckCircle',
        color: 'var(--color-muted-foreground)',
        text: 'Analysis complete',
        bgColor: 'bg-muted',
        textColor: 'text-muted-foreground'
      };
    }
    return {
      icon: 'FileText',
      color: 'var(--color-primary)',
      text: autoAnalyze ? 'Auto-analyzing...' : 'Ready to analyze',
      bgColor: 'bg-primary/10',
      textColor: 'text-primary'
    };
  };

  if (files?.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-2xl mx-auto mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Files" size={20} color="var(--color-foreground)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            {autoAnalyze ? 'Processing Queue' : 'Upload Queue'}
          </h3>
          <span className="px-2 py-1 bg-primary/10 text-primary text-sm font-mono rounded-md">
            {files?.length}
          </span>
        </div>
        {autoAnalyze && (
          <div className="flex items-center space-x-1 text-xs text-primary">
            <Icon name="Zap" size={12} color="var(--color-primary)" />
            <span>Auto-Analyze Enabled</span>
          </div>
        )}
      </div>
      {/* File List */}
      <div className="space-y-3 mb-6">
        {files?.map((file, index) => {
          const fileIcon = getFileIcon(file?.name);
          const status = getFileStatus(file);

          return (
            <div
              key={`${file?.name}-${index}`}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
            >
              <div className="flex items-center space-x-4">
                {/* File Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon 
                      name={fileIcon?.name} 
                      size={20} 
                      color={fileIcon?.color} 
                      strokeWidth={2}
                    />
                  </div>
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-body font-medium text-card-foreground truncate">
                      {file?.name}
                    </h4>
                    <span className="text-sm text-muted-foreground font-mono">
                      {formatFileSize(file?.size)}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="flex items-center space-x-2">
                    <div className={`
                      flex items-center space-x-1.5 px-2 py-1 rounded-md text-xs font-body font-medium
                      ${status.bgColor} ${status.textColor}
                    `}>
                      <Icon 
                        name={status.icon} 
                        size={12} 
                        color={status.color}
                        className={status.animate ? 'animate-spin' : ''}
                      />
                      <span>{status.text}</span>
                    </div>

                    {file?.progress !== undefined && (
                      <div className="flex-1 max-w-32">
                        <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-300 ease-smooth"
                            style={{ width: `${file?.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveFile(index)}
                    iconName="X"
                    iconSize={16}
                    disabled={isProcessing && file?.status === 'processing'}
                    className="text-muted-foreground hover:text-error"
                  />
                </div>
              </div>
              {/* Error Details */}
              {file?.status === 'error' && file?.error && (
                <div className="mt-3 p-3 bg-error/5 border border-error/20 rounded-md">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                    <div className="text-sm text-error font-body">
                      <div className="font-medium mb-1">Upload Error</div>
                      <div className="text-error/80 mb-2">{file?.error}</div>
                      
                      {/* OCR Help Message - Show for most PDF processing errors */}
                      {(() => {
                        console.log('🔍 Checking error for OCR help:', file?.error);
                        const errorText = file?.error?.toLowerCase() || '';
                        const fileName = file?.name?.toLowerCase() || '';
                        
                        // Show OCR help for PDF files that failed processing (most are likely OCR issues)
                        const isPDFFile = fileName.includes('.pdf');
                        const isProcessingError = (
                          errorText.includes('scanned') || 
                          errorText.includes('no text extracted') || 
                          errorText.includes('insufficient text') ||
                          errorText.includes('ocr') ||
                          errorText.includes('text from pdf') ||
                          errorText.includes('extract') ||
                          errorText.includes('image-based') ||
                          errorText.includes('corrupted') ||
                          errorText.includes('failed to extract') ||
                          errorText.includes('pdf may be') ||
                          errorText.includes('readable text') ||
                          errorText.includes('processing error') ||
                          errorText.includes('failed to') ||
                          (isPDFFile && errorText.includes('error')) // Generic error for PDF files
                        );
                        
                        console.log('🔍 Is PDF file?', isPDFFile);
                        console.log('🔍 Is processing error?', isProcessingError);
                        return isPDFFile && isProcessingError;
                      })() && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-start space-x-2">
                            <Icon name="Info" size={16} color="#3b82f6" />
                            <div className="text-sm">
                              <div className="font-medium text-blue-900 mb-2">PDF Processing Failed</div>
                              <div className="text-blue-800 mb-3">
                                This PDF may be scanned or image-based. Try converting it to searchable text:
                              </div>
                              <a 
                                href="https://tools.pdf24.org/en/ocr-pdf" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm"
                              >
                                <Icon name="ExternalLink" size={14} color="white" />
                                <span>Convert Scanned PDF</span>
                              </a>
                              <div className="text-xs text-blue-700 mt-2">
                                This free tool will convert your image-based PDF to searchable text that our system can process.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Action Buttons - Only show manual analyze if not auto-analyze */}
      <div className="flex items-center justify-center space-x-4">
        {!autoAnalyze && (
          <Button
            variant="default"
            size="lg"
            onClick={() => {
              // This will be handled by the parent component
              const event = new CustomEvent('startAnalysis');
              window.dispatchEvent(event);
            }}
            iconName="Play"
            iconPosition="left"
            iconSize={20}
            disabled={isProcessing || files?.filter(f => f?.status !== 'error')?.length === 0}
            loading={isProcessing && files?.some(f => f?.status === 'processing')}
          >
            {isProcessing ? 'Processing...' : 'Analyze'}
          </Button>
        )}
        
        <Button
          variant="outline"
          size="lg"
          onClick={onClearAll}
          iconName="Trash2"
          iconPosition="left"
          iconSize={20}
          disabled={isProcessing}
        >
          Clear Queue
        </Button>
      </div>
    </div>
  );
};

export default FileQueue;
